import React, { useState, useEffect, useCallback } from 'react';
import Joyride from 'react-joyride';
import { HelpCircle, X, Plus, Trash2 } from 'lucide-react';
import axiosInstance from '../services/axiosInstance';

// ShadCN UI Components
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';

const StockEntryForm = ({ isOpen, onClose, onSave }) => {
  const [entries, setEntries] = useState([getInitialEntry()]);
  const [sectors, setSectors] = useState([]);
  const [sectorInputs, setSectorInputs] = useState(['']);
  const [showSectorSuggestions, setShowSectorSuggestions] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  
  // Stock suggestion states
  const [stockSuggestions, setStockSuggestions] = useState([]);
  const [showStockSuggestions, setShowStockSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  const steps = [
    { target: '.stock-name-input', content: 'Start by entering the stock name here', disableBeacon: true },
    { target: '.sector-input', content: 'Enter or select the sector for this stock. New sectors will be automatically added.' },
    { target: '.actions-group', content: 'Select the action you want to perform with this stock - Buy, Sell, or add to Watchlist' },
    { target: '.buy-details', content: 'If you selected Buy, enter the purchase details here' },
    { target: '.sell-details', content: 'If you selected Sell, enter the selling details here' },
    { target: '.watch-details', content: 'If you selected Watch, enter your target price and action' },
    { target: '.save-button', content: 'Finally, click here to save all your entries' },
  ];

  useEffect(() => { setSectorInputs(entries.map(e => e.sector || '')); }, [entries.length]);
  useEffect(() => { fetchSectors(); }, []);
  
  // Debug scrolling when entries change
  useEffect(() => {
    console.log('🔄 Entries changed! Total entries:', entries.length);
    entries.forEach((entry, idx) => {
      console.log(`📝 Entry ${idx + 1}:`, {
        buyDetails: entry.buyDetails?.length || 0,
        sellDetails: entry.sellDetails?.length || 0,
        watching: entry.actions.watching
      });
    });
    
    // Check scrollable area after a short delay to let DOM update
    setTimeout(() => {
      const scrollableDiv = document.querySelector('[data-scrollable="true"]');
      if (scrollableDiv) {
        console.log('🔍 Scrollable div found after entries change:');
        console.log('📏 Scroll Height:', scrollableDiv.scrollHeight);
        console.log('👁️ Client Height:', scrollableDiv.clientHeight);
        console.log('📊 Can scroll:', scrollableDiv.scrollHeight > scrollableDiv.clientHeight);
        
        // Force set overflow properties to override any CSS
        scrollableDiv.style.setProperty('overflow', 'auto', 'important');
        scrollableDiv.style.setProperty('overflow-y', 'auto', 'important');
        scrollableDiv.style.setProperty('overflow-x', 'hidden', 'important');
        
        console.log('🔧 Forced overflow properties set!');
      }
    }, 100);
  }, [entries]);
  
  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.stock-suggestions') && 
          !event.target.closest('.stock-name-wrapper')) {
        setShowStockSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);
  
  // Clear suggestions when form is closed
  useEffect(() => {
    if (!isOpen) {
      setShowStockSuggestions(false);
      setStockSuggestions([]);
      setLoadingSuggestions(false);
    }
  }, [isOpen]);

  // Reset form when opened and handle scroll lock
  useEffect(() => {
    if (isOpen) {
      // Reset form to initial state when opened
      setEntries([getInitialEntry()]);
      setSectorInputs(['']);
      setShowSectorSuggestions(false);
      setShowStockSuggestions(false);
      setStockSuggestions([]);
      setLoadingSuggestions(false);
      setSelectedSuggestionIndex(-1);
      
      // Clear any pending timers
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        setDebounceTimer(null);
      }
    }
  }, [isOpen]);

  // Reset form when closing
  const handleClose = () => {
    // Reset form to initial state
    setEntries([getInitialEntry()]);
    setSectorInputs(['']);
    setShowSectorSuggestions(false);
    setShowStockSuggestions(false);
    setStockSuggestions([]);
    setLoadingSuggestions(false);
    setSelectedSuggestionIndex(-1);
    
    // Clear any pending timers
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      setDebounceTimer(null);
    }
    
    // Close the form
    onClose();
  };

  function getInitialEntry() {
    return {
      stockName: '',
      sector: '',
      exchange: '', // Start with no exchange selected
      actions: { buy: false, sell: false, watching: false },
      buyDetails: [{ price: '', date: '', time: '', quantity: '' }],
      sellDetails: [{ price: '', date: '', time: '', quantity: '' }],
      watchDetails: { targetPrice: '', action: 'buy' }
    };
  }
  
  async function fetchSectors() {
    try {
      const response = await axiosInstance.get('/stocks/sectors');
      setSectors(response.data.sectors || []);
    } catch { setSectors([]); }
  }
  
  // Debounced function to fetch stock suggestions
  const debouncedFetchSuggestions = useCallback((query, exchange) => {
    // Clear any existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    // Clear previous suggestions immediately when starting new search
    setStockSuggestions([]);
    setShowStockSuggestions(false);
    
    const timer = setTimeout(() => {
      fetchStockSuggestions(query, exchange);
    }, 300); // 300ms delay
    
    setDebounceTimer(timer);
  }, [debounceTimer]);
  
  // Function to fetch stock suggestions
  const fetchStockSuggestions = async (query, exchange = '', limit = 20) => {
    if (!query || query.length < 2) {
      setStockSuggestions([]);
      setShowStockSuggestions(false);
      return;
    }
    
    try {
      setLoadingSuggestions(true);
      const params = new URLSearchParams({
        q: query,
        limit: limit
      });
      
      if (exchange) {
        params.append('exchange', exchange);
      }
      
      const response = await axiosInstance.get(`/stocks/suggestions?${params}`);
      
      if (response.data.success) {
        const suggestions = response.data.suggestions || [];
        setStockSuggestions(suggestions);
        setShowStockSuggestions(suggestions.length > 0);
      } else {
        setStockSuggestions([]);
        setShowStockSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching stock suggestions:', error);
      setStockSuggestions([]);
      setShowStockSuggestions(false);
    } finally {
      setLoadingSuggestions(false);
    }
  };
  
  // Function to handle stock name input change
  const handleStockNameChange = (idx, value) => {
    handleEntryChange(idx, 'stockName', value);
    
    // Clear suggestions immediately if input is too short or no exchange selected
    if (!value || value.length < 2 || !entries[idx].exchange) {
      setStockSuggestions([]);
      setShowStockSuggestions(false);
      // Clear any pending debounced search
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        setDebounceTimer(null);
      }
      return;
    }
    
    // Use debounced function to fetch suggestions
    const exchange = entries[idx].exchange;
    debouncedFetchSuggestions(value, exchange);
  };
  
  // Function to select a stock suggestion
  const handleStockSelect = (idx, stock) => {
    handleEntryChange(idx, 'stockName', stock.symbol);
    handleEntryChange(idx, 'exchange', stock.exchange);
    setShowStockSuggestions(false);
    setStockSuggestions([]);
  };
  
  // Function to handle exchange change
  const handleExchangeChange = (idx, value) => {
    handleEntryChange(idx, 'exchange', value);
    
    // Clear stock name and suggestions when exchange changes
    handleEntryChange(idx, 'stockName', '');
    setStockSuggestions([]);
    setShowStockSuggestions(false);
    
    // Clear any pending debounced search
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      setDebounceTimer(null);
    }
  };
  
  // Function to handle keyboard navigation in suggestions
  const handleStockNameKeyDown = (idx, e) => {
    if (!showStockSuggestions || stockSuggestions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < stockSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : stockSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < stockSuggestions.length) {
          handleStockSelect(idx, stockSuggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowStockSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
      default:
        break;
    }
  };
  
  // Reset selected index when suggestions change
  useEffect(() => {
    setSelectedSuggestionIndex(-1);
  }, [stockSuggestions]);
  
  // Function to handle input blur
  const handleStockNameBlur = (e) => {
    // Check if the blur is happening because user clicked on a suggestion
    const relatedTarget = e.relatedTarget || e.explicitOriginalTarget || document.activeElement;
    
    // If clicking on a suggestion, don't hide the suggestions yet
    if (relatedTarget && relatedTarget.closest('.stock-suggestions')) {
      return;
    }
    
    // Use setTimeout to allow click events on suggestions to fire first
    setTimeout(() => {
      setShowStockSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }, 200);
  };

  const handleAddEntry = () => setEntries([...entries, getInitialEntry()]);
  const handleRemoveEntry = idx => setEntries(entries.filter((_, i) => i !== idx));
  const handleEntryChange = (idx, field, value, detailIdx = 0) => {
    const newEntries = [...entries];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      if (Array.isArray(newEntries[idx][parent])) {
        newEntries[idx][parent][detailIdx][child] = value;
      } else {
        newEntries[idx][parent][child] = value;
      }
    } else {
      newEntries[idx][field] = value;
    }
    setEntries(newEntries);
  };
  const handleAddBuyEntry = idx => {
    const newEntries = [...entries];
    newEntries[idx].buyDetails.push({ price: '', date: '', time: '', quantity: '' });
    setEntries(newEntries);
    console.log('🟦 Added buy entry! Total buy details:', newEntries[idx].buyDetails.length);
  };
  const handleAddSellEntry = idx => {
    const newEntries = [...entries];
    newEntries[idx].sellDetails.push({ price: '', date: '', time: '', quantity: '' });
    setEntries(newEntries);
    console.log('🟥 Added sell entry! Total sell details:', newEntries[idx].sellDetails.length);
  };
  const handleRemoveDetailEntry = (idx, type, detailIdx) => {
    const newEntries = [...entries];
    newEntries[idx][type] = newEntries[idx][type].filter((_, i) => i !== detailIdx);
    setEntries(newEntries);
  };

  const handleSectorInputChange = async (idx, value) => {
    const newSectorInputs = [...sectorInputs];
    newSectorInputs[idx] = value;
    setSectorInputs(newSectorInputs);
    const hasMatches = sectors.some(sector =>
      sector.toLowerCase().includes((value || '').toLowerCase())
    );
    setShowSectorSuggestions(hasMatches);
    handleEntryChange(idx, 'sector', value);
  };
  const handleSectorSelect = async (idx, sector) => {
    handleEntryChange(idx, 'sector', sector);
    const newSectorInputs = [...sectorInputs];
    newSectorInputs[idx] = sector;
    setSectorInputs(newSectorInputs);
    setShowSectorSuggestions(false);

    // Add the sector to the database only when user selects it
    if (sector && !sectors.includes(sector)) {
      try {
        const response = await axiosInstance.post('/stocks/sectors', { sector: sector });
        if (response.status === 200 || response.status === 201) {
          setSectors([...sectors, sector]);
        }
      } catch (error) {
        console.error('Error adding sector:', error);
      }
    }
  };

  const handleSave = async () => {
    try {
      // Validate that all entries have required fields
      const validationErrors = [];
      entries.forEach((entry, index) => {
        if (!entry.exchange) {
          validationErrors.push(`Entry ${index + 1}: Exchange is required`);
        }
        if (!entry.stockName) {
          validationErrors.push(`Entry ${index + 1}: Stock name is required`);
        }
        if (!entry.sector) {
          validationErrors.push(`Entry ${index + 1}: Sector is required`);
        }
      });
      
      if (validationErrors.length > 0) {
        alert(`Please fix the following errors:\n${validationErrors.join('\n')}`);
        return;
      }
      
      // Process entries to combine date and time
      const processedEntries = entries.map(entry => {
        const processedEntry = { ...entry };
        
        // Process buy details
        if (processedEntry.buyDetails) {
          processedEntry.buyDetails = processedEntry.buyDetails.map(detail => {
            const processedDetail = { ...detail };
            if (detail.date && detail.time) {
              // Combine date and time into a single datetime string
              const dateTimeString = `${detail.date}T${detail.time}`;
              processedDetail.date = dateTimeString;
              delete processedDetail.time; // Remove time field as it's now combined
            } else if (detail.date) {
              // If only date is provided, keep it as is
              processedDetail.date = detail.date;
              delete processedDetail.time;
            } else {
              // If neither date nor time is provided, set empty string
              processedDetail.date = '';
              delete processedDetail.time;
            }
            return processedDetail;
          });
        }
        
        // Process sell details
        if (processedEntry.sellDetails) {
          processedEntry.sellDetails = processedEntry.sellDetails.map(detail => {
            const processedDetail = { ...detail };
            if (detail.date && detail.time) {
              // Combine date and time into a single datetime string
              const dateTimeString = `${detail.date}T${detail.time}`;
              processedDetail.date = dateTimeString;
              delete processedDetail.time; // Remove time field as it's now combined
            } else if (detail.date) {
              // If only date is provided, keep it as is
              processedDetail.date = detail.date;
              delete processedDetail.time;
            } else {
              // If neither date nor time is provided, set empty string
              processedDetail.date = '';
              delete processedDetail.time;
            }
            return processedDetail;
          });
        }
        
        return processedEntry;
      });
      
      const response = await axiosInstance.post('/stocks/add', { entries: processedEntries });
      if (response.status === 200 || response.status === 201) {
        // Reset form after successful save
        setEntries([getInitialEntry()]);
        setSectorInputs(['']);
        setShowSectorSuggestions(false);
        setShowStockSuggestions(false);
        setStockSuggestions([]);
        setLoadingSuggestions(false);
        setSelectedSuggestionIndex(-1);
        
        // Clear any pending timers
        if (debounceTimer) {
          clearTimeout(debounceTimer);
          setDebounceTimer(null);
        }
        
        onSave?.();
        onClose();
      }
    } catch (error) {
      console.error('Error saving entries:', error);
    }
  };

  const handleShowGuide = () => {
    if (!showGuide && window.confirm('Would you like a guided tour of this form?')) setShowGuide(true);
  };
  const handleJoyrideCallback = data => {
    const { status } = data;
    if (status === 'finished' || status === 'skipped') setShowGuide(false);
  };

  return (
    <div style={{ 
      all: 'unset', 
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#111827',
      backgroundColor: 'transparent'
    }}>
      <Joyride
        steps={steps}
        run={showGuide}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep={false}
        scrollOffset={200}
        disableScrolling={false}
        callback={handleJoyrideCallback}
        styles={{
          options: { primaryColor: '#007bff', zIndex: 2000, arrowColor: '#fff' },
        }}
        floaterProps={{
          disableAnimation: true,
          styles: { wrapper: { zIndex: 2000, position: 'fixed' } }
        }}
        tooltipComponent={({
          continuous, index, step, backProps, primaryProps, skipProps, size, isLastStep
        }) => (
          <div className="bg-white rounded-lg p-4 shadow-lg max-w-sm">
            <div className="mb-3 text-sm font-semibold text-gray-800">
              Step {index + 1}/{size}
            </div>
            <div className="mb-4 text-sm leading-relaxed text-gray-600">
              {step.content}
            </div>
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                {index > 0 && (
                  <Button {...backProps} variant="outline" size="sm">
                    ← Back
                  </Button>
                )}
                <Button {...primaryProps} size="sm">
                  {isLastStep ? 'Finish Tour ✨' : 'Next →'}
                </Button>
              </div>
              {!isLastStep && (
                <Button {...skipProps} variant="ghost" size="sm">
                  Skip Tour
                </Button>
              )}
            </div>
          </div>
        )}
      />
      
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-3xl h-[90vh] flex flex-col bg-white text-gray-900 border border-gray-300 overflow-hidden shadow-2xl" style={{ maxHeight: '90vh' }}>
          <DialogHeader className="flex-shrink-0 border-b border-gray-200 pb-4 bg-white z-10">
            <DialogTitle className="text-xl font-semibold text-gray-900">
              Add Stock Entry
            </DialogTitle>
            <div className="absolute top-4 right-4 flex gap-2">
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleShowGuide}
                title="Show help guide"
                className="border-gray-300 hover:bg-gray-100 text-gray-700"
              >
                <HelpCircle className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleClose}
                className="h-8 w-8 p-0 hover:bg-red-100 hover:scale-110 transition-all duration-200 bg-red-50 border border-red-200 text-red-600 hover:text-red-700 hover:border-red-300"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          
          <div 
            data-scrollable="true"
            className="flex-1 px-6 py-4 space-y-6 min-h-0 border-l-2 border-blue-200" 
            style={{ 
              maxHeight: 'calc(90vh - 140px)',
              overflow: 'auto',
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollbarWidth: 'thin',
              scrollbarColor: '#d1d5db #f3f4f6',
              // Force these properties with !important equivalent
              position: 'relative',
              display: 'block',
              // Additional CSS properties to ensure scrolling works
              height: 'auto',
              minHeight: '0',
              flexShrink: '1'
            }}
            onScroll={(e) => {
              console.log('🔍 Scroll event triggered!');
              console.log('📏 Scroll Top:', e.target.scrollTop);
              console.log('📐 Scroll Height:', e.target.scrollHeight);
              console.log('👁️ Client Height:', e.target.clientHeight);
              console.log('📊 Can scroll:', e.target.scrollHeight > e.target.clientHeight);
            }}
                         ref={(el) => {
               if (el) {
                 console.log('🎯 Scrollable div ref set!');
                 console.log('📏 Element scrollHeight:', el.scrollHeight);
                 console.log('👁️ Element clientHeight:', el.clientHeight);
                 console.log('📊 Should scroll:', el.scrollHeight > el.clientHeight);
                 
                 const computedStyle = window.getComputedStyle(el);
                 console.log('🎨 Computed overflow:', computedStyle.overflow);
                 console.log('🎨 Computed overflowY:', computedStyle.overflowY);
                 console.log('🎨 Computed overflowX:', computedStyle.overflowX);
                 console.log('📋 Inline styles:', el.style.cssText);
                 
                 // Test if scrolling actually works
                 if (el.scrollHeight > el.clientHeight) {
                   console.log('🧪 Testing scroll functionality...');
                   el.scrollTop = 100;
                   console.log('🧪 Scroll position after setting scrollTop:', el.scrollTop);
                 }
               }
             }}
          >
            {entries.map((entry, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm">
                {/* Entry Header */}
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-semibold text-lg text-gray-900 font-sans">
                    Entry {index + 1}
                  </h3>
                  {entries.length > 1 && (
                    <Button 
                      variant="destructive" 
                      size="icon"
                      onClick={() => handleRemoveEntry(index)}
                      className="bg-red-500 hover:bg-red-600 text-white border-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Basic Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <Label htmlFor={`exchange-${index}`} className="text-sm font-medium text-gray-700 mb-2 block">Exchange *</Label>
                    <Select
                      value={entry.exchange}
                      onValueChange={value => handleExchangeChange(index, value)}
                    >
                      <SelectTrigger className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500">
                        <SelectValue placeholder="Select Exchange First" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-300">
                        <SelectItem value="NSE" className="text-gray-900 hover:bg-gray-100">NSE</SelectItem>
                        <SelectItem value="BSE" className="text-gray-900 hover:bg-gray-100">BSE</SelectItem>
                        <SelectItem value="MCX" className="text-gray-900 hover:bg-gray-100">MCX</SelectItem>
                        <SelectItem value="NFO" className="text-gray-900 hover:bg-gray-100">NFO</SelectItem>
                        <SelectItem value="CDS" className="text-gray-900 hover:bg-gray-100">CDS</SelectItem>
                        <SelectItem value="BFO" className="text-gray-900 hover:bg-gray-100">BFO</SelectItem>
                        <SelectItem value="INDICES" className="text-gray-900 hover:bg-gray-100">INDICES</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-blue-600 mt-1">Select exchange to enable stock search</p>
                  </div>

                  <div className="stock-name-wrapper relative">
                    <Label htmlFor={`stock-name-${index}`} className="text-sm font-medium text-gray-700 mb-2 block">
                      Stock Name {!entry.exchange && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id={`stock-name-${index}`}
                      className={`stock-name-input mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500 ${!entry.exchange ? 'opacity-50 cursor-not-allowed border-red-300' : ''}`}
                      placeholder={entry.exchange ? "Enter stock name" : "Select exchange first"}
                      value={entry.stockName}
                      onChange={e => handleStockNameChange(index, e.target.value)}
                      onKeyDown={e => handleStockNameKeyDown(index, e)}
                      onBlur={handleStockNameBlur}
                      disabled={!entry.exchange}
                    />
                    {!entry.exchange && (
                      <p className="text-xs text-red-500 mt-1">Please select an exchange first to search for stocks</p>
                    )}
                    {showStockSuggestions && (
                      <div className="stock-suggestions absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto z-50 mt-1">
                        {loadingSuggestions ? (
                          <div className="p-3 text-center text-gray-500 text-sm">
                            Searching stocks...
                          </div>
                        ) : stockSuggestions.length === 0 ? (
                          <div className="p-3 text-center text-gray-500 text-sm">
                            No stocks found
                          </div>
                        ) : (
                          <>
                            <div className="px-3 py-2 bg-gray-50 border-b text-xs text-gray-600 font-medium">
                              Found {stockSuggestions.length} stocks
                            </div>
                            {stockSuggestions.map((stock, suggestionIndex) => (
                                                             <div
                                key={stock.symbol}
                                className={`px-3 py-2 cursor-pointer border-b border-gray-100 hover:bg-gray-50 ${
                                  suggestionIndex === selectedSuggestionIndex ? 'bg-blue-50' : ''
                                }`}
                                onMouseDown={(e) => {
                                  e.preventDefault(); // Prevent blur event
                                  handleStockSelect(index, stock);
                                }}
                                onClick={() => handleStockSelect(index, stock)}
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <div className="font-medium text-sm">{stock.symbol}</div>
                                    <div className="text-xs text-gray-500">{stock.name}</div>
                                  </div>
                                  <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded">
                                    {stock.exchange}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>



                  <div className="relative">
                    <Label htmlFor={`sector-${index}`} className="text-sm font-medium text-gray-700 mb-2 block">Sector</Label>
                    <Input
                      id={`sector-${index}`}
                      className="sector-input mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Type to search sectors"
                      value={sectorInputs[index]}
                      onChange={e => handleSectorInputChange(index, e.target.value)}
                      onFocus={() => setShowSectorSuggestions(true)}
                    />
                    {showSectorSuggestions &&
                      sectors.some(sector =>
                        sector.toLowerCase().includes((sectorInputs[index] || '').toLowerCase())
                      ) && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg max-h-32 overflow-y-auto z-30 mt-1">
                        {sectors
                          .filter(sector =>
                            sector.toLowerCase().includes((sectorInputs[index] || '').toLowerCase())
                          )
                          .map(sector => (
                          <div
                            key={sector}
                            className="px-3 py-2 cursor-pointer border-b border-gray-100 hover:bg-gray-50 text-sm"
                            onClick={() => handleSectorSelect(index, sector)}
                          >
                            {sector}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor={`watch-price-${index}`} className="text-sm font-medium text-gray-700 mb-2 block">Watch Price</Label>
                    <Input
                      id={`watch-price-${index}`}
                      type="number"
                      className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Enter watch price"
                      value={entry.watchDetails.targetPrice}
                      onChange={e => handleEntryChange(index, 'watchDetails.targetPrice', e.target.value)}
                    />
                  </div>
                </div>

                {/* Action Checkboxes */}
                <div className="actions-group flex flex-wrap gap-6 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`buy-${index}`}
                      checked={entry.actions.buy}
                      onCheckedChange={checked => handleEntryChange(index, 'actions.buy', checked)}
                      className="border-gray-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                    <Label htmlFor={`buy-${index}`} className="font-medium text-gray-900 cursor-pointer">Buy</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`sell-${index}`}
                      checked={entry.actions.sell}
                      onCheckedChange={checked => handleEntryChange(index, 'actions.sell', checked)}
                      className="border-gray-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                    />
                    <Label htmlFor={`sell-${index}`} className="font-medium text-gray-900 cursor-pointer">Sell</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`watching-${index}`}
                      checked={entry.actions.watching}
                      onCheckedChange={checked => handleEntryChange(index, 'actions.watching', checked)}
                      className="border-gray-400 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                    />
                    <Label htmlFor={`watching-${index}`} className="font-medium text-gray-900 cursor-pointer">Watching</Label>
                  </div>
                </div>

                {/* Buy Details */}
                {entry.actions.buy && entry.buyDetails.map((buyDetail, buyIndex) => (
                  <div key={buyIndex} className="buy-details border border-blue-300 rounded-lg p-4 mb-4 bg-blue-50">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-semibold text-blue-800 text-base">
                        Buy Entry {buyIndex + 1}
                      </h4>
                      {entry.buyDetails.length > 1 && (
                        <Button 
                          variant="destructive" 
                          size="icon"
                          onClick={() => handleRemoveDetailEntry(index, 'buyDetails', buyIndex)}
                          className="bg-red-500 hover:bg-red-600 text-white border-0 h-8 w-8"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                      <div>
                        <Label>Price</Label>
                        <Input
                          type="number"
                          className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Enter price"
                          value={buyDetail.price}
                          onChange={e => handleEntryChange(index, 'buyDetails.price', e.target.value, buyIndex)}
                        />
                      </div>
                      <div>
                        <Label>Quantity</Label>
                        <Input
                          type="number"
                          className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Enter quantity"
                          value={buyDetail.quantity}
                          onChange={e => handleEntryChange(index, 'buyDetails.quantity', e.target.value, buyIndex)}
                        />
                      </div>
                      <div>
                        <Label>Date</Label>
                        <Input
                          type="date"
                          className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          value={buyDetail.date}
                          onChange={e => handleEntryChange(index, 'buyDetails.date', e.target.value, buyIndex)}
                        />
                      </div>
                      <div>
                        <Label>Time</Label>
                        <Input
                          type="time"
                          className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          value={buyDetail.time}
                          onChange={e => handleEntryChange(index, 'buyDetails.time', e.target.value, buyIndex)}
                        />
                      </div>
                    </div>
                    {buyIndex === entry.buyDetails.length - 1 && (
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddBuyEntry(index)}
                        className="text-blue-700 border-blue-400 hover:bg-blue-100 bg-white font-medium"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Another Buy Entry
                      </Button>
                    )}
                  </div>
                ))}

                {/* Sell Details */}
                {entry.actions.sell && entry.sellDetails.map((sellDetail, sellIndex) => (
                  <div key={sellIndex} className="sell-details border border-red-200 rounded-lg p-4 mb-3 bg-red-50">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-red-800">
                        Sell Entry {sellIndex + 1}
                      </h4>
                      {entry.sellDetails.length > 1 && (
                        <Button 
                          variant="destructive" 
                          size="icon"
                          onClick={() => handleRemoveDetailEntry(index, 'sellDetails', sellIndex)}
                          className="bg-red-500 hover:bg-red-600 text-white border-0 h-8 w-8"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                      <div>
                        <Label>Price</Label>
                        <Input
                          type="number"
                          className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Enter price"
                          value={sellDetail.price}
                          onChange={e => handleEntryChange(index, 'sellDetails.price', e.target.value, sellIndex)}
                        />
                      </div>
                      <div>
                        <Label>Quantity</Label>
                        <Input
                          type="number"
                          className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Enter quantity"
                          value={sellDetail.quantity}
                          onChange={e => handleEntryChange(index, 'sellDetails.quantity', e.target.value, sellIndex)}
                        />
                      </div>
                      <div>
                        <Label>Date</Label>
                        <Input
                          type="date"
                          className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          value={sellDetail.date}
                          onChange={e => handleEntryChange(index, 'sellDetails.date', e.target.value, sellIndex)}
                        />
                      </div>
                      <div>
                        <Label>Time</Label>
                        <Input
                          type="time"
                          className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          value={sellDetail.time}
                          onChange={e => handleEntryChange(index, 'sellDetails.time', e.target.value, sellIndex)}
                        />
                      </div>
                    </div>
                    {sellIndex === entry.sellDetails.length - 1 && (
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddSellEntry(index)}
                        className="text-red-600 border-red-300 hover:bg-red-100 bg-white font-medium"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Another Sell Entry
                      </Button>
                    )}
                  </div>
                ))}

                {/* Watch Details */}
                {entry.actions.watching && (
                  <div className="watch-details border border-purple-200 rounded-lg p-4 bg-purple-50">
                    <h4 className="font-medium text-purple-800 mb-3">
                      Watch Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label>Target Price</Label>
                        <Input
                          type="number"
                          className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Enter target price"
                          value={entry.watchDetails.targetPrice}
                          onChange={e => handleEntryChange(index, 'watchDetails.targetPrice', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Action</Label>
                        <Select
                          value={entry.watchDetails.action}
                          onValueChange={value => handleEntryChange(index, 'watchDetails.action', value)}
                        >
                          <SelectTrigger className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-300">
                            <SelectItem value="buy" className="text-gray-900 hover:bg-gray-100">Buy</SelectItem>
                            <SelectItem value="sell" className="text-gray-900 hover:bg-gray-100">Sell</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer Actions */}
          <div className="flex justify-between items-center pt-4 pb-4 px-6 border-t border-gray-200 flex-shrink-0 bg-white">
            <Button 
              variant="outline"
              onClick={handleAddEntry}
              className="flex items-center gap-2 border-gray-300 text-gray-700 hover:bg-gray-50 bg-white font-medium"
            >
              <Plus className="h-4 w-4" />
              Add Another Entry
            </Button>
            <Button 
              onClick={handleSave}
              className="save-button bg-blue-600 hover:bg-blue-700 text-white border-0 font-medium px-6"
            >
              Save All Entries
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockEntryForm;
